import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : process.env.REACT_APP_API_URL || '/api';
const XTREAM_URL = process.env.REACT_APP_XTREAM_URL || 'https://fluxivo.de';

const Success = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [m3uUrl, setM3uUrl] = useState('');
  const [xtreamData, setXtreamData] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedXtream, setCopiedXtream] = useState({});

  useEffect(() => {
    const checkPayment = async () => {
      // Récupérer le token depuis l'URL ou localStorage
      let token = searchParams.get('token') || localStorage.getItem('payment_token');
      
      if (!token) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/payments/status/${token}`);
        
	        if (response.data.success && response.data.status === 'paid') {
	          setStatus('success');
          setPaymentData(response.data);
          setM3uUrl(response.data.m3uUrl);
          setReceiptUrl(response.data.receiptDownloadUrl || response.data.receiptUrl || `/api/payments/receipt/${encodeURIComponent(token)}?download=1`);
          const xtream = response.data.xtream;
	          setXtreamData(xtream ? {
	            url: xtream.url || xtream.xtreamUrl || XTREAM_URL,
	            username: xtream.username || xtream.xtreamUsername,
	            password: xtream.password || xtream.xtreamPassword
	          } : null);
	          localStorage.removeItem('payment_token');
        } else if (response.data.status === 'pending') {
          setStatus('pending');
          setTimeout(checkPayment, 5000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Erreur vérification paiement:', error);
        setStatus('failed');
      }
    };

    checkPayment();
  }, [searchParams]);

  const copyToClipboard = (text, field = 'main') => {
    navigator.clipboard.writeText(text);
    if (field === 'main') {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } else {
      setCopiedXtream({...copiedXtream, [field]: true});
      setTimeout(() => setCopiedXtream({...copiedXtream, [field]: false}), 3000);
    }
  };

  const downloadM3U = () => {
    window.open(m3uUrl, '_blank');
  };

  const downloadReceipt = () => {
    if (receiptUrl) window.location.href = receiptUrl;
  };

  if (status === 'checking') {
    return (
      <div className="success-container">
        <div className="status-card checking">
          <div className="spinner"></div>
          <h2>Vérification du paiement...</h2>
          <p>Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="success-container">
        <div className="status-card pending">
          <div className="spinner"></div>
          <h2>⏳ Paiement en cours</h2>
          <p>Votre paiement est en cours de traitement.</p>
          <p>Vous recevrez vos accès par email dès validation.</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="success-container">
        <div className="status-card failed">
          <div className="icon-failed">❌</div>
          <h2>Paiement échoué</h2>
          <p>Une erreur est survenue lors du paiement.</p>
          <button className="retry-button" onClick={() => window.location.href = '/plans'}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="success-container">
      <nav className="success-navbar">
        <div className="logo" onClick={() => window.location.href = '/'}>Fluxivo</div>
      </nav>

      <div className="success-content">
        <div className="success-card">
          <div className="success-icon">✅</div>
          <h1>Paiement réussi !</h1>
          <p className="success-message">
            Merci {paymentData?.nom} ! Votre abonnement est maintenant actif.
          </p>

          {/* Section Xtream Codes - Recommandée */}
          {xtreamData && (
            <div className="xtream-section featured">
              <div className="section-header">
                <h2>🎯 Vos identifiants Xtream Codes</h2>
                <span className="recommended-badge">Recommandé</span>
              </div>
              <p className="section-description">
                Utilisez ces identifiants pour une meilleure expérience dans votre application IPTV
              </p>

              <div className="credentials-grid">
                <div className="credential-item">
                  <label>🌐 URL du serveur</label>
                  <div className="credential-value">
                    <input
                      type="text"
                      value={xtreamData.url}
                      readOnly
                      className="credential-input"
                    />
                    <button 
                      onClick={() => copyToClipboard(xtreamData.url, 'url')} 
                      className="copy-btn-small"
                    >
                      {copiedXtream.url ? '✓' : '📋'}
                    </button>
                  </div>
                </div>

                <div className="credential-item">
                  <label>👤 Nom d'utilisateur</label>
                  <div className="credential-value">
                    <input
                      type="text"
                      value={xtreamData.username}
                      readOnly
                      className="credential-input"
                    />
                    <button 
                      onClick={() => copyToClipboard(xtreamData.username, 'username')} 
                      className="copy-btn-small"
                    >
                      {copiedXtream.username ? '✓' : '📋'}
                    </button>
                  </div>
                </div>

                <div className="credential-item">
                  <label>🔑 Mot de passe</label>
                  <div className="credential-value">
                    <input
                      type="text"
                      value={xtreamData.password}
                      readOnly
                      className="credential-input"
                    />
                    <button 
                      onClick={() => copyToClipboard(xtreamData.password, 'password')} 
                      className="copy-btn-small"
                    >
                      {copiedXtream.password ? '✓' : '📋'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="xtream-instructions">
                <h4>Configuration Xtream Codes :</h4>
                <ol>
                  <li>Téléchargez <strong>IPTV Smarters Pro</strong> ou une application compatible</li>
                  <li>Sélectionnez <strong>"Login with Xtream Codes API"</strong></li>
                  <li>Entrez les identifiants ci-dessus</li>
                  <li>Profitez de toutes vos chaînes !</li>
                </ol>
              </div>
            </div>
          )}

          {/* Section M3U - Alternative */}
          <div className="m3u-section">
            <h2>📺 Alternative : Lien M3U</h2>
            <p className="section-description">
              Vous pouvez aussi utiliser ce lien M3U direct dans votre application IPTV
            </p>

            <div className="m3u-url-container">
              <input
                type="text"
                value={m3uUrl}
                readOnly
                className="m3u-input"
              />
              <button onClick={() => copyToClipboard(m3uUrl)} className="copy-button">
                {copied ? '✓ Copié' : '📋 Copier'}
              </button>
            </div>

            <button onClick={downloadM3U} className="download-button">
              📥 Télécharger le fichier M3U
            </button>
          </div>

          <div className="instructions-section">
            <h3>📱 Applications recommandées</h3>
            <div className="apps-grid">
              <div className="app-item">
                <strong>Android/iOS</strong>
                <p>IPTV Smarters Pro, TiviMate, GSE Smart IPTV</p>
              </div>
              <div className="app-item">
                <strong>PC/Mac</strong>
                <p>VLC Media Player, IPTV Smarters</p>
              </div>
              <div className="app-item">
                <strong>Smart TV</strong>
                <p>Smart IPTV, SS IPTV</p>
              </div>
            </div>
          </div>

          <div className="email-info">
            <p>📧 Un email de confirmation avec toutes les informations a été envoyé à <strong>{paymentData?.email}</strong></p>
            <p className="email-note">Vérifiez aussi vos spams si vous ne le trouvez pas</p>
          </div>

          {receiptUrl && (
            <div className="m3u-section">
              <h2>🧾 Reçu de paiement</h2>
              <p className="section-description">
                Le reçu contient le détail du paiement et vos accès Xtream/M3U.
              </p>
              <button onClick={downloadReceipt} className="download-button">
                Télécharger le reçu
              </button>
            </div>
          )}

          <button className="home-button" onClick={() => window.location.href = '/'}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
};

export default Success;
